import React from "react";
// Collections Management Dashboard

// Custom
import Layout from "../../layout";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

// Svg
import Download from "../../assets/svg/download";

// Utils
import { firstLetterUppercase } from "../../utils/helper";

// React icons
import { BiDollar } from "react-icons/bi";
import { FaRegClock } from "react-icons/fa";
import { FaBan } from "react-icons/fa";
import { LuFileCheck } from "react-icons/lu";
import { LuMapPin } from "react-icons/lu";
import { CiUser } from "react-icons/ci";
import { FaFilter } from "react-icons/fa";
import { MdOutlineFileDownload } from "react-icons/md";
import { IconType } from "react-icons";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

// Types
interface StatsData {
  title: string;
  amount: string;
  subTitle: string;
  icon: IconType;
}

interface ZoneStat {
  zone: string;
  totalAccounts: number;
  amount: number;
  noticeStages: {
    firstNotice: number;
    finalNotice: number;
    readyForDisconnect: number;
  };
}

interface BulkAction {
  title: string;
  icon: IconType;
}

interface NoticeStage {
  title: string;
  value: number;
}

interface SelectOption {
  value: string;
  label: string;
}

const stats = {
  totalOutstanding: 1245680.5,
  totalAccounts: 3568,
  overdueAccounts: 890,
  disconnectEligible: 678,
  averageDaysOverdue: 45,
  totalLateFees: 234560.75,
};

const statsData: StatsData[] = [
  {
    title: "Total Outstanding",
    amount: "1245680.50",
    subTitle: "3568 accounts",
    icon: BiDollar,
  },
  {
    title: "Overdue Accounts",
    amount: "890",
    subTitle: "Avg 45 days late",
    icon: FaRegClock,
  },
  {
    title: "Disconnect Eligible",
    amount: "678",
    subTitle: "Total accounts",
    icon: FaBan,
  },
  {
    title: "Late Fees",
    amount: "234560.75",
    subTitle: "This billing cycle",
    icon: LuFileCheck,
  },
];

const zoneStats: ZoneStat[] = [
  {
    zone: "North Zone",
    totalAccounts: 245,
    amount: 234567.8,
    noticeStages: {
      firstNotice: 120,
      finalNotice: 85,
      readyForDisconnect: 40,
    },
  },
  {
    zone: "South Zone",
    totalAccounts: 189,
    amount: 198450.6,
    noticeStages: {
      firstNotice: 95,
      finalNotice: 64,
      readyForDisconnect: 30,
    },
  },
  {
    zone: "East Zone",
    totalAccounts: 156,
    amount: 167890.45,
    noticeStages: {
      firstNotice: 80,
      finalNotice: 56,
      readyForDisconnect: 20,
    },
  },
  {
    zone: "West Zone",
    totalAccounts: 88,
    amount: 145670.25,
    noticeStages: {
      firstNotice: 45,
      finalNotice: 28,
      readyForDisconnect: 15,
    },
  },
];

const bulkActions: BulkAction[] = [
  { title: "Generate Notice Batch", icon: CiUser },
  { title: "Schedule Disconnections", icon: LuFileCheck },
  { title: "Export Reports", icon: MdOutlineFileDownload },
  { title: "Filter Queue", icon: FaFilter },
];

const noticeStages: NoticeStage[] = [
  { title: "pendingFirstNotice", value: 340 },
  { title: "firstNoticeSent", value: 180 },
  { title: "pendingFinalNotice", value: 145 },
  { title: "finalNoticeSent", value: 110 },
  { title: "readyForDisconnect", value: 105 },
  { title: "scheduledForDisconnect", value: 98 },
];

const zoneOptions: SelectOption[] = [
  { value: "all", label: "All" },
  { value: "north", label: "North Zone" },
  { value: "south", label: "South Zone" },
  { value: "east", label: "East Zone" },
  { value: "west", label: "West Zone" },
];

const CollectionsManagementDashboard: React.FC = () => {
  const statsCard = ({
    title,
    amount,
    subTitle,
    icon,
  }: {
    title: string;
    amount: string;
    subTitle: string;
    icon: IconType;
  }) => {
    return (
      <Card>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Stack>
              <Typography variant="h5">{title}</Typography>
              <Typography variant="body1" mt={"10px"} fontWeight={700}>
                {amount}
              </Typography>
              <Typography variant="caption" mt={"10px"}>
                {subTitle}
              </Typography>
            </Stack>
            <UnifiedIcon icon={icon} color="#999B9D" fontSize="30px" />
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const chipColor = (status: string): string => {
    if (status.includes("Ready") || status.includes("Scheduled"))
      return "#9CE976";
    if (status.includes("Final")) return "#edb64a";
    return "#0396FF";
  };

  return (
    <Layout>
      <Card>
        <CardContent>
          <Typography variant="h5">Collections Management</Typography>
          <Typography variant="caption" color="#7D808F" mt={"2px"}>
            Utility Bill Collections Dashboard
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2.25} mt={"20px"}>
        {statsData.map((item: StatsData, index: number) => {
          return (
            <Grid key={index} size={{ xs: 12, md: 3, lg: 3 }}>
              {statsCard(item)}
            </Grid>
          );
        })}
      </Grid>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Payment Methods Distribution</Typography>

              <Stack
                direction={"row"}
                width={"80%"}
                justifyContent={"flex-end"}
                alignItems={"center"}
                gap={"20px"}
              >
                <Box width={"25%"}>
                  <CustomOutlinedSelect
                    placeholder={"Filter by Zone"}
                    options={zoneOptions}
                  />
                </Box>
                <Download />
              </Stack>
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25} mt={"20px"}>
            {noticeStages.map((item: NoticeStage, index: number) => (
              <Grid key={index} size={{ xs: 12, md: 4, lg: 4 }}>
                <Card sx={{ border: "1px solid #D7DAE7", padding: "16px" }}>
                  <Stack direction={"column"} gap={"8px"}>
                    <Typography
                      variant="body1"
                      fontSize={"16px"}
                      fontWeight={600}
                    >
                      {firstLetterUppercase(item.title)}
                    </Typography>
                    <Typography variant="caption" fontSize={"14px"}>
                      {item.value}
                    </Typography>
                  </Stack>

                  <Box mt={"10px"}>
                    <Box
                      sx={{
                        width: `${(item.value / stats.overdueAccounts) * 100}%`,
                        height: "10px",
                        borderRadius: "8px",
                        background: chipColor(item.title),
                      }}
                    />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Zone-wise Collection Status</Typography>

              <ContainedButton
                label={"View Details"}
                sx={{
                  fontWeight: 600,
                  color: "#ffffff",
                }}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25} mt={"20px"}>
            {zoneStats.map((item: ZoneStat, index: number) => (
              <Grid key={index} size={{ xs: 12, md: 4, lg: 4 }}>
                <Card sx={{ border: "1px solid #D7DAE7", padding: "16px" }}>
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Stack direction={"row"} gap={"8px"} alignItems={"center"}>
                      <UnifiedIcon
                        icon={LuMapPin}
                        color="#999B9D"
                        fontSize="20px"
                      />
                      <Typography
                        variant="body1"
                        fontSize={"16px"}
                        fontWeight={600}
                      >
                        {item.zone}
                      </Typography>
                    </Stack>

                    <Typography variant="body1">
                      {item.totalAccounts}
                    </Typography>
                  </Stack>

                  <Typography variant="body1" mt={"15px"}>
                    {" "}
                    <Typography component="caption" color="#FF3B30">
                      ${item.amount.toLocaleString()}
                    </Typography>{" "}
                    outstanding
                  </Typography>

                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    mt={"15px"}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      First Notice
                    </Typography>
                    <Typography variant="caption">
                      {item.noticeStages.firstNotice}
                    </Typography>
                  </Stack>

                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    mt={"15px"}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      Final Notice
                    </Typography>
                    <Typography variant="caption">
                      {item.noticeStages.finalNotice}
                    </Typography>
                  </Stack>

                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    mt={"15px"}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      Ready for Disconnect
                    </Typography>
                    <Typography variant="caption">
                      {item.noticeStages.readyForDisconnect}
                    </Typography>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Bulk Actions</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            {bulkActions.map((item: BulkAction, index: number) => (
              <Grid key={index} size={{ xs: 12, md: 4, lg: 4 }}>
                <Card sx={{ border: "1px solid #D7DAE7", padding: "16px" }}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography
                      variant="body1"
                      fontSize={"16px"}
                      fontWeight={600}
                    >
                      {item?.title}
                    </Typography>

                    <UnifiedIcon
                      icon={item.icon}
                      color="#999B9D"
                      fontSize="22px"
                    />
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default CollectionsManagementDashboard;
